// Phase I — Activity heatmap (GitHub-style) для вкладки Аналитика.
//
// Два паттерна на одном блоке (тоггл сверху):
//   * Производительность — by_completion: «сколько прогнал N мая».
//     Default ON (это первое что хотел оператор).
//   * Покрытие наблюдений — by_observation: «за какие RINEX-даты
//     было тестирование». Phase D-индикатор — расширилось ли
//     покрытие за пределы 2026-01-05.
//
// Сетка: столбцы=недели (понедельник→воскресенье вертикально),
// строки=дни недели. Диапазон [min_date, today] — берём min из
// текущей выборки, today из локали оператора (UTC чтобы не пляшут
// границы дня). Пустые дни заполняем bleach-квадратами.
//
// Цвет: 5-бакетная шкала (0 / 1-10 / 11-50 / 51-150 / 150+) под
// максимум прода 161 (Phase I §0.2). Зашкаливающие 300+ при серии
// 300 на день уйдут в верхний бакет — никаких HSL-интерполяций,
// дискретные ступени читаются лучше на маленьких квадратах.
//
// Клик: тап на квадрат показывает плашку «DD мес YYYY · Всего N
// · DONE M · FAILED K». Закрытие — повторный тап / тап на другой
// квадрат / клик вне таблицы.

import { useEffect, useMemo, useRef, useState } from 'react';

import type { ActivityDay } from '../../api/types';

import './ActivityHeatmap.css';

type Axis = 'completion' | 'observation';

const RU_MONTHS = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
] as const;
const RU_MONTHS_FULL = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const;
const RU_WEEKDAYS_SHORT = ['Пн', 'Ср', 'Пт'] as const;

interface Props {
  byCompletion: ActivityDay[];
  byObservation: ActivityDay[];
}

interface Bucket {
  /** Inclusive lower bound for `total`. */
  min: number;
  className: string;
  /** Operator-facing legend label. */
  label: string;
}

// Phase I §0.2 — bucket thresholds calibrated to the prod max of
// ~161 jobs/day (2026-05-20). 150+ catches the future «серия 300
// за день» spikes without an extra band; small batches (1-10) are
// distinguishable from idle days (0).
const BUCKETS: Bucket[] = [
  { min: 0, className: 'activity-cell-b0', label: '0' },
  { min: 1, className: 'activity-cell-b1', label: '1–10' },
  { min: 11, className: 'activity-cell-b2', label: '11–50' },
  { min: 51, className: 'activity-cell-b3', label: '51–150' },
  { min: 151, className: 'activity-cell-b4', label: '150+' },
];

function bucketFor(total: number): Bucket {
  for (let i = BUCKETS.length - 1; i >= 0; i -= 1) {
    if (total >= (BUCKETS[i] as Bucket).min) {
      return BUCKETS[i] as Bucket;
    }
  }
  return BUCKETS[0] as Bucket;
}

/** ISO YYYY-MM-DD → operator-readable «DD месяца YYYY». */
function formatRuDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const day = d ?? 0;
  const monthIdx = Math.min(Math.max((m ?? 1) - 1, 0), 11);
  return `${day} ${RU_MONTHS_FULL[monthIdx]} ${y}`;
}

/** Today in UTC as ISO YYYY-MM-DD. UTC keeps the right-edge of the
 *  heatmap stable regardless of operator's local timezone. */
function todayIso(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Inclusive list of every ISO date between `start` and `end`. */
function dateRange(start: string, end: string): string[] {
  const out: string[] = [];
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return out;
  const cursor = new Date(s);
  while (cursor.getTime() <= e.getTime()) {
    const y = cursor.getUTCFullYear();
    const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    const d = String(cursor.getUTCDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/** Day-of-week index where Monday=0, Sunday=6 (GitHub-style: weeks
 *  start on a Sunday but rows are Mon-Sun visually; we go ISO and
 *  start the column on its first ISO weekday). */
function isoDow(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  // JS getUTCDay: 0=Sun..6=Sat → shift to Mon=0..Sun=6.
  return (d.getUTCDay() + 6) % 7;
}

interface WeekColumn {
  /** ISO date of the column's anchor (Monday or the first day in the
   *  range if it starts mid-week). Drives month-label placement. */
  anchorDate: string;
  /** 7 slots Mon..Sun; null = before the range start or after today. */
  days: (ActivityDay | null)[];
}

/** Build columns of weeks for the GitHub-style grid. Column anchor
 *  is the Monday of the week (or the first in-range day, when the
 *  range starts mid-week). */
function buildWeekColumns(
  byDate: Map<string, ActivityDay>,
  start: string,
  end: string,
): WeekColumn[] {
  const all = dateRange(start, end);
  if (all.length === 0) return [];
  const columns: WeekColumn[] = [];
  let current: WeekColumn | null = null;
  for (const iso of all) {
    const dow = isoDow(iso);
    if (dow === 0 || current === null) {
      current = {
        anchorDate: iso,
        days: [null, null, null, null, null, null, null],
      };
      columns.push(current);
    }
    current.days[dow] = byDate.get(iso) ?? {
      date: iso,
      total: 0,
      done: 0,
      failed: 0,
    };
  }
  return columns;
}

/** Find the operator-facing first column of each month (the first
 *  Monday that lands in that month). */
function monthLabels(columns: WeekColumn[]): Array<{ idx: number; label: string }> {
  const out: Array<{ idx: number; label: string }> = [];
  let lastMonth = -1;
  columns.forEach((col, idx) => {
    const m = Number(col.anchorDate.slice(5, 7)) - 1;
    if (m !== lastMonth) {
      out.push({ idx, label: RU_MONTHS[m] ?? '' });
      lastMonth = m;
    }
  });
  return out;
}

export function ActivityHeatmap({ byCompletion, byObservation }: Props) {
  const [axis, setAxis] = useState<Axis>('completion');
  const [selected, setSelected] = useState<ActivityDay | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const data = axis === 'completion' ? byCompletion : byObservation;

  // Phase I §0.1 — diapason is [min from data, today]. NOT a full
  // calendar year — heatmaps over an empty 2025 second half are
  // useless and waste screen space.
  const { weeks, labels, rangeStart, rangeEnd } = useMemo(() => {
    const byDate_ = new Map<string, ActivityDay>();
    for (const d of data) byDate_.set(d.date, d);
    if (data.length === 0) {
      return {
        weeks: [] as WeekColumn[],
        labels: [] as Array<{ idx: number; label: string }>,
        rangeStart: '',
        rangeEnd: '',
      };
    }
    let minDate = data[0]!.date;
    for (const d of data) if (d.date < minDate) minDate = d.date;
    const today = todayIso();
    // Defensive — if data dates lie in the future, extend the range
    // to cover them (test fixtures regularly use 2026 dates).
    let endDate = today;
    for (const d of data) if (d.date > endDate) endDate = d.date;
    const weeks_ = buildWeekColumns(byDate_, minDate, endDate);
    return {
      weeks: weeks_,
      labels: monthLabels(weeks_),
      rangeStart: minDate,
      rangeEnd: endDate,
    };
  }, [data]);

  // Close the popup on outside click. Listener bound only while a
  // popup is open so we don't pay the cost on idle blocks.
  useEffect(() => {
    if (selected === null) return;
    const onDocClick = (e: MouseEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(e.target as Node)) {
        setSelected(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [selected]);

  const onCellClick = (day: ActivityDay) => {
    setSelected((prev) =>
      prev !== null && prev.date === day.date ? null : day,
    );
  };

  return (
    <div
      className="activity-section"
      ref={rootRef}
      data-testid="activity-heatmap"
    >
      <div className="section-title">активность по дням</div>

      <div className="activity-toggle" role="group" aria-label="Ось активности">
        <button
          type="button"
          className={`activity-toggle-btn ${axis === 'completion' ? 'active' : ''}`}
          onClick={() => {
            setAxis('completion');
            setSelected(null);
          }}
          aria-pressed={axis === 'completion'}
          data-testid="activity-toggle-completion"
        >
          производительность
        </button>
        <button
          type="button"
          className={`activity-toggle-btn ${axis === 'observation' ? 'active' : ''}`}
          onClick={() => {
            setAxis('observation');
            setSelected(null);
          }}
          aria-pressed={axis === 'observation'}
          data-testid="activity-toggle-observation"
        >
          покрытие наблюдений
        </button>
      </div>

      {weeks.length === 0 ? (
        <div className="analytics-empty">нет данных за выбранный период</div>
      ) : (
        <>
          <div className="activity-grid-wrap">
            <div
              className="activity-grid"
              role="grid"
              aria-label={`Активность по дням, ${rangeStart} — ${rangeEnd}`}
            >
              {/* Month-label row sits above the columns. */}
              <div className="activity-months-row">
                <div className="activity-corner" />
                {weeks.map((_, idx) => {
                  const label = labels.find((l) => l.idx === idx);
                  return (
                    <div
                      key={`m${idx}`}
                      className="activity-month-cell"
                      data-testid={
                        label ? `activity-month-${label.label}` : undefined
                      }
                    >
                      {label?.label ?? ''}
                    </div>
                  );
                })}
              </div>
              {/* Day-of-week row label + 7 day rows. */}
              {[0, 1, 2, 3, 4, 5, 6].map((dow) => (
                <div key={`row-${dow}`} className="activity-row">
                  <div className="activity-dow-cell">
                    {dow === 0
                      ? RU_WEEKDAYS_SHORT[0]
                      : dow === 2
                        ? RU_WEEKDAYS_SHORT[1]
                        : dow === 4
                          ? RU_WEEKDAYS_SHORT[2]
                          : ''}
                  </div>
                  {weeks.map((col, ci) => {
                    const day: ActivityDay | null = col.days[dow] ?? null;
                    if (day === null) {
                      return (
                        <div
                          key={`c${ci}-${dow}`}
                          className="activity-cell activity-cell-out"
                          aria-hidden="true"
                        />
                      );
                    }
                    const bucket = bucketFor(day.total);
                    const isSelected = selected?.date === day.date;
                    return (
                      <button
                        key={`c${ci}-${dow}`}
                        type="button"
                        className={`activity-cell ${bucket.className} ${
                          isSelected ? 'selected' : ''
                        }`}
                        onClick={() => onCellClick(day)}
                        title={`${day.date}: ${day.total}`}
                        data-testid={`activity-cell-${day.date}`}
                        data-bucket={bucket.label}
                        aria-label={`${day.date}, ${day.total} задач`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Legend mirrors HeatmapStationConfig's swatch row so the
              Аналитика-screen rhythm stays consistent. */}
          <div className="activity-legend">
            <span className="activity-legend-label">меньше</span>
            {BUCKETS.map((b) => (
              <span
                key={b.className}
                className={`activity-legend-swatch ${b.className}`}
                title={b.label}
              />
            ))}
            <span className="activity-legend-label">больше</span>
          </div>

          {selected && (
            <div
              className="activity-popup"
              role="status"
              data-testid="activity-popup"
            >
              <div className="activity-popup-date">
                {formatRuDate(selected.date)}
              </div>
              {selected.total === 0 ? (
                <div className="activity-popup-empty">нет задач</div>
              ) : (
                <div className="activity-popup-counts">
                  <span>
                    Всего: <strong>{selected.total}</strong>
                  </span>
                  <span className="activity-popup-sep">·</span>
                  <span>
                    DONE: <strong>{selected.done}</strong>
                  </span>
                  <span className="activity-popup-sep">·</span>
                  <span>
                    FAILED: <strong>{selected.failed}</strong>
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
